/**
 * Data-plane entry: Supabase dual-run OR Hono staged path (VITE_API_BASE_URL).
 * When Hono API base is set, auth/storage/realtime do not require Supabase client.
 */
import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { isHonoApiMode } from './apiClient'
import { honoAuth, honoRealtime, honoStorage } from './honoDataPlane'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined
const isTestMode = import.meta.env.MODE === 'test'
const honoMode = isHonoApiMode()

const resolvedSupabaseUrl =
  supabaseUrl || (isTestMode && !honoMode ? 'http://localhost:54321' : undefined)
const resolvedSupabaseAnonKey =
  supabaseAnonKey || (isTestMode && !honoMode ? 'test-anon-key' : undefined)

let supabase: SupabaseClient | null = null

if (!honoMode) {
  if (!resolvedSupabaseUrl || !resolvedSupabaseAnonKey) {
    throw new Error('Missing Supabase environment variables')
  }
  supabase = createClient(resolvedSupabaseUrl, resolvedSupabaseAnonKey)
}

/**
 * AUTHENTICATION — Supabase GoTrue or Hono staged session token.
 */
export const auth = honoMode ? (honoAuth as unknown as SupabaseClient['auth']) : supabase!.auth

/**
 * FUNCTIONS — prefer api.ts invokeFunction (routes through Hono when flagged).
 */
export const functions = honoMode
  ? ({
      invoke: async () => {
        throw new Error('Use invokeFunction from api.ts (Hono path)')
      },
    } as unknown as SupabaseClient['functions'])
  : supabase!.functions

/**
 * STORAGE — Supabase storage or Hono presign via evidence API.
 */
export const storage = honoMode
  ? (honoStorage as unknown as SupabaseClient['storage'])
  : supabase!.storage

/**
 * REALTIME — Supabase channels or Hono SSE.
 */
export const realtime = honoMode
  ? honoRealtime
  : {
      channel: supabase!.channel.bind(supabase!),
      removeChannel: supabase!.removeChannel.bind(supabase!),
    }

export const config = {
  supabaseUrl: resolvedSupabaseUrl ?? '',
  supabaseAnonKey: resolvedSupabaseAnonKey ?? '',
  honoMode,
  apiBaseUrl: honoMode ? (import.meta.env.VITE_API_BASE_URL as string) : undefined,
}
